import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Section, Container, Typography, Grid, Anchor, Form, Button } from 'components/base';
import {
  BlogCarousel,
  CategoryCard,
  CloudinaryPicture,
  ProductCard,
  PrismicHtmlContent,
} from 'components/rich';
import httpRequest from 'utils/httpRequest';
import { isEmailValid } from 'utils/validator';

import SETTINGS from '../../settings';
import { ProductDetailLink, CategoryPageLink, AboutPageLink } from '../pageLinks';

export const ProductsSection = props => {
  const colProps = {
    xs: props.xs,
    sm: props.sm,
    md: props.md,
    lg: props.lg,
    xl: props.xl,
  };

  const cardClassName = classNames({
    'card-equalize': true,
    'card-mobile-compact': colProps.xs === 6,
    'card-img-square': true,
  });

  const subCategories = props.subCategories.map(category => (
    <Grid.Col key={category.id} {...colProps}>
      <CategoryPageLink category={category}>
        <CategoryCard asLink category={category} className={cardClassName} />
      </CategoryPageLink>
    </Grid.Col>
  ));

  const products = props.products.map(product => (
    <Grid.Col key={product.id} {...colProps}>
      <ProductDetailLink productId={product.id}>
        <ProductCard asLink product={product} className={cardClassName} />
      </ProductDetailLink>
    </Grid.Col>
  ));

  return (
    <Section>
      <Container>
        {props.title && <Typography.H2 className="section-title mb-2">{props.title}</Typography.H2>}
        <Grid.Row>
          {props.productsFirst ? (
            <>
              {products}
              {subCategories}
            </>
          ) : (
            <>
              {subCategories}
              {products}
            </>
          )}
        </Grid.Row>
      </Container>
    </Section>
  );
};

ProductsSection.propTypes = {
  title: PropTypes.string,
  subCategories: PropTypes.array,
  products: PropTypes.array,
  productsFirst: PropTypes.bool,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
};

ProductsSection.defaultProps = {
  subCategories: [],
  products: [],
  productsFirst: false,
};

export const CategoriesSection = props => {
  const colProps = {
    xs: props.xs,
    sm: props.sm,
    md: props.md,
    lg: props.lg,
    xl: props.xl,
  };

  return (
    <Section>
      <Container>
        <Typography.H2 className="section-title mb-2">{props.title}</Typography.H2>
        <Grid.Row>
          {props.categories.map(category => (
            <Grid.Col key={category.id} {...colProps}>
              <CategoryPageLink category={category}>
                <CategoryCard asLink category={category} className="card-equalize" />
              </CategoryPageLink>
            </Grid.Col>
          ))}
        </Grid.Row>
      </Container>
    </Section>
  );
};

CategoriesSection.propTypes = {
  title: PropTypes.string,
  categories: PropTypes.array,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
};

export const MottoSection = props => {
  return (
    <Section type={props.type}>
      <Container>
        <Grid.Row>
          <Grid.Col xs={12} md={9} className="offset-md-1">
            <span className="small text-warning font-weight-bold text-uppercase">
              {props.label}
            </span>
            <Typography.H2 className="mt-1 mb-6">{props.motto}</Typography.H2>
            <AboutPageLink>
              <Anchor className="btn btn-warning">Learn More</Anchor>
            </AboutPageLink>
          </Grid.Col>
        </Grid.Row>
      </Container>
    </Section>
  );
};

MottoSection.propTypes = {
  type: PropTypes.oneOf(['green', 'orange', 'brown']),
  label: PropTypes.string,
  motto: PropTypes.string,
};

export const ReferencesSection = props => {
  return (
    <Section className="container">
      <Typography.H2 className="section-title mb-2">AS FEATURED IN</Typography.H2>
      <Grid.Row className="mt-6">
        <Grid.Col xs={12} md={6} lg={4} className="col-logo">
          <img src="static/img/logo-new-york-times.png" alt="" />
        </Grid.Col>
        <Grid.Col xs={12} md={6} lg={4} className="col-logo">
          <img src="static/img/logo-good-housekeeping.png" alt="" />
        </Grid.Col>
        <Grid.Col xs={12} md={6} lg={4} className="col-logo">
          <img src="static/img/logo-brit-and-co.png" alt="" />
        </Grid.Col>
        <Grid.Col xs={12} md={6} lg={4} className="col-logo">
          <img src="static/img/logo-parents.png" alt="" />
        </Grid.Col>
        <Grid.Col xs={12} md={6} lg={4} className="col-logo">
          <img src="static/img/logo-better-homes-gardens.png" alt="" />
        </Grid.Col>
        <Grid.Col xs={12} md={6} lg={4} className="col-logo">
          <img src="static/img/logo-refinery29.png" alt="" />
        </Grid.Col>
      </Grid.Row>
    </Section>
  );
};

export const InstagramSection = props => {
  return (
    <Section type="brown">
      <Container>
        <Typography.H3 className="section-title">#parabopress</Typography.H3>
        <div className="text-center">
          <Typography.P className="text-light mb-3">
            We love seeing your photos in print!
          </Typography.P>
          <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="link-arrow text-brown">
            FOLLOW US ON INSTAGRAM
          </Anchor>
        </div>
        <Grid.Row className="mt-5 row-instagram">
          <Grid.Col xs={6} md={3}>
            <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
              <CloudinaryPicture imageKey="instagram/instagram1" />
            </Anchor>
          </Grid.Col>
          <Grid.Col xs={6} md={3}>
            <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
              <CloudinaryPicture imageKey="instagram/instagram2" />
            </Anchor>
          </Grid.Col>
          <Grid.Col xs={6} md={3}>
            <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
              <CloudinaryPicture imageKey="instagram/instagram3" />
            </Anchor>
          </Grid.Col>
          <Grid.Col xs={6} md={3}>
            <Anchor href={SETTINGS.INSTAGRAM_URL} targetBlank className="img-instagram-container">
              <CloudinaryPicture imageKey="instagram/instagram4" />
            </Anchor>
          </Grid.Col>
        </Grid.Row>
      </Container>
    </Section>
  );
};

export const BlogSection = props => {
  return (
    <Section>
      <Container>
        <Typography.H2 className="section-title mb-2">FROM OUR BLOG</Typography.H2>
        <BlogCarousel className="carousel-blog">
          {props.data.map((article, idx) => (
            <BlogCarousel.Item
              key={article.uid}
              index={idx}
              imageKey={article.imageKey}
              title={article.caption}
              url={article.link}
            >
              <div className="card-text">
                <PrismicHtmlContent content={article.description} />
              </div>
              <span className="link-arrow">READ MORE</span>
            </BlogCarousel.Item>
          ))}
        </BlogCarousel>
        <div className="text-center">
          <Button type="outline" asLink href="/blog">
            View All Articles
          </Button>
        </div>
      </Container>
    </Section>
  );
};

BlogSection.propTypes = {
  data: PropTypes.array,
};

export const NewsletterSection = props => {
  return (
    <Section type="green" className={props.className}>
      <Container className="text-center mb-5">
        <Typography.H2 className="section-title">SAVE 10% ON YOUR FIRST ORDER!</Typography.H2>
        <Typography.P className="text-light mt-2 mb-5">
          Be the first to find out when we have sales, add new products, or send along home decor
          inspo.
        </Typography.P>

        <Button asLink href={SETTINGS.NEWSLETTER_URL} targetBlank type="success">
          Subscribe To Our Newsletter
        </Button>
      </Container>
    </Section>
  );
};

NewsletterSection.propTypes = {
  className: PropTypes.string,
};
